<template>
  <div class="user-dropdown-container" ref="dropdownContainer">
    <button
      @click="toggleDropdown"
      class="user-dropdown-trigger"
      :class="{ 'active': isOpen }"
      type="button"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
      <svg class="w-4 h-4 ml-1 transition-transform duration-200" :class="{ 'rotate-180': isOpen }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div v-if="isOpen" class="user-dropdown-menu" @click.stop>
      <div class="dropdown-header" v-if="currentUser">
        <div class="user-name">{{ currentUser.full_name }}</div>
        <div class="user-email">{{ currentUser.email }}</div>
        <span class="plan-badge" :class="isPro ? 'plan-badge--pro' : 'plan-badge--free'">
          {{ isPro ? 'Pro' : 'Free' }}
        </span>
      </div>

      <div class="dropdown-divider" />

      <router-link to="/profile" class="dropdown-item" @click="closeDropdown">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        Profile
      </router-link>

      <!-- Free user: upgrade CTA -->
      <router-link v-if="!isPro" to="/pricing" class="dropdown-item dropdown-item--upgrade" @click="closeDropdown">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
        Upgrade to Pro
      </router-link>

      <!-- Pro user: billing portal -->
      <button v-if="isPro" class="dropdown-item" type="button" @click="handleBilling">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
        Billing
      </button>

      <div class="dropdown-divider" />

      <button @click="handleLogout" class="dropdown-item logout-item" type="button">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Logout
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useSubscription } from '../composables/useSubscription.js'

defineProps({
  currentUser: { type: Object, required: true }
})

const emit = defineEmits(['logout'])

// isPro comes from the shared subscription store (populated by useAuth on
// every auth check) so the badge is correct on every page, not only ones
// that fetch subscription data themselves.
const { openPortal, isPro } = useSubscription()
const isOpen = ref(false)
const dropdownContainer = ref(null)

const toggleDropdown = () => { isOpen.value = !isOpen.value }
const closeDropdown = () => { isOpen.value = false }

const handleLogout = () => { closeDropdown(); emit('logout') }

const handleBilling = async () => {
  closeDropdown()
  await openPortal()
}

const handleClickOutside = (event) => {
  if (dropdownContainer.value && !dropdownContainer.value.contains(event.target)) {
    closeDropdown()
  }
}

const handleKeyDown = (event) => {
  if (event.key === 'Escape') closeDropdown()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<style scoped>
.user-dropdown-container {
  position: relative;
  display: inline-block;
}

.user-dropdown-trigger {
  @apply flex items-center px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 rounded-lg hover:border-primary transition-colors;
}

.user-dropdown-trigger.active {
  @apply border-primary text-zinc-900;
}

.user-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 50;
  min-width: 210px;
  margin-top: 0.5rem;
  @apply bg-white border border-zinc-100 rounded-xl py-1;
  animation: dropdownSlideIn 0.15s ease-out;
}

@keyframes dropdownSlideIn {
  from { opacity: 0; transform: translateY(-5px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.dropdown-header {
  @apply px-4 py-3;
}

.user-name {
  @apply text-sm font-semibold text-zinc-900 truncate;
}

.user-email {
  @apply text-xs text-zinc-400 truncate mt-0.5;
}

.plan-badge {
  @apply inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded-full border;
}

.plan-badge--free {
  @apply border-zinc-200 text-zinc-500;
}

.plan-badge--pro {
  @apply border-primary/30 text-primary bg-primary/5;
}

.dropdown-divider {
  @apply border-t border-zinc-100 my-1;
}

.dropdown-item {
  @apply flex items-center gap-2 w-full px-4 py-2 text-sm text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50 transition-colors text-left;
  border: none;
  background: none;
  cursor: pointer;
  text-decoration: none;
}

.dropdown-item--upgrade {
  @apply text-primary hover:bg-primary/5 font-medium;
}

.logout-item {
  @apply text-red-500 hover:bg-red-50 hover:text-red-600;
}
</style>
